import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as SearchIcon } from "../../assets/search.svg";
import {
  TextField,
  Typography,
  Box,
  useMediaQuery,
  Paper,
  InputAdornment,
  Divider,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Select,
  MenuItem
} from "@mui/material";
import useThemeColor from "../../utils/useTheme";
import { useTheme } from "@emotion/react";
import {
  selectAllMealOrRecipeData,
  selectCurrentAddingMeal,
  selectCurrentAddingRecipe
} from "../../features/level3/level3Selectors";
import formatNumber from "../../utils/formatNumber";
import { deleteCustomFoodItem, deleteMealRecipe } from "../../api/level3Api";
import {
  getAllMealOrRecipeData,
  gettingMealData,
  gettingRecipeData,
  updateAddingMeal,
  updateAddingRecipe
} from "../../features/level3/level3Slice";
import { convertItemQuantityToGrams } from "../../utils/weightConvert";
import LongMenu from "./ThreeDotMenu";
import DeleteUserModal from "../Modals/DeleteUserModal";
import ServicingRecipeModal from "../Modals/ServicingRecipeModal";
import { addMealOrRecipeToDiary } from "../../api/diaryApi";
import dayjs from "dayjs";
import { getCurrentUserDiaries } from "../../features/diary/diarySlice";
import { Add } from "@mui/icons-material";
import { addNotificationUser } from "../../features/auth/authSlice";

const excludedFields = [
  "serving_id",
  "serving_description",
  "serving_url",
  "metric_serving_unit"
];

const processMeals = (meals, searchValue, type) => {
  return meals
    .map((meal) => {
      const total = meal.items.reduce(
        (acc, item) => {
          if (
            item.type === "800g" ||
            type === "custom food" ||
            item.details?.isCustomFood
          )
            acc.metric_serving_amount += parseFloat(
              item.details.serving.metric_serving_amount
            );
          acc.calories += parseFloat(item.details.serving.calories);
          acc.protein += parseFloat(item.details.serving.protein);
          return acc;
        },
        { metric_serving_amount: 0, calories: 0, protein: 0 }
      );

      return {
        _id: meal._id,
        name: meal.name,
        servicing_per_recipe: meal.servicing_per_recipe,
        recipeType: meal.recipeType,
        metric_serving_amount: total.metric_serving_amount.toFixed(2),
        metric_serving_unit: "g",
        calories: total.calories.toFixed(2),
        protein: total.protein.toFixed(2),
        ...meal
      };
    })
    .filter((item) =>
      searchValue
        ? item.name?.toLowerCase()?.includes(searchValue?.toLowerCase())
        : Boolean(item.name)
    )
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
};
const AddMealOrRecipeToDiary = ({
  levelId,
  activeDay,
  currentLevel,
  type,
  setEditing,
  setEditMealToDiary,
  setIsShowingCreateMeal,
  setIsRefreshNeeded,
  setIsShowingCreateRecipe,
  setEditRecipeToDiary,
  handleResetAmount,
  setAddingRecipeToDiary,
  addingRecipeToDiary,
  customFoodFormData,
  setCustomFoodFormData,
  setIsShowingCreateCustomFood,
  setEditCustomFoodToDiary,
  setShowCustomFoodAdjustment,
  selectedCustomFoodItem,
  setSelectedCustomFoodItem,
  showCustomFoodAdjustment,
  setSelectedOptionData,
  setSelectedCustomFood
}) => {
  const [searchValue, setSearchValue] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteCustomFoodItemId, setDeleteCustomFoodItemId] = useState("");
  const styles = useThemeColor();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const allMealOrRecipeData = useSelector(selectAllMealOrRecipeData);

  const [adjustmentFormData, setAdjustmentFormData] = useState({
    quantity: "",
    noOfQty: "",
    unit: "grams"
  });
  const [adjustmentDisabledFields, setAdjustmentDisabledFields] = useState({
    quantity: false,
    noOfQty: false,
    unit: false
  });

  const handleAdjustmentInputChange = (e) => {
    const { name, value } = e.target;
    setAdjustmentFormData({
      ...adjustmentFormData,
      [name]: value
    });
  };

  const handleAdjustmentQuantityChange = (e) => {
    const value = e.target.value;
    if (Number(value) >= 0 && +value <= 9999) {
      handleAdjustmentInputChange(e);
      // setAdjustmentDisabledFields({
      //   quantity: false,
      //   noOfQty: value === "" ? false : true,
      //   unit: false
      // });
    }
  };

  const handleAdjustmentNoOfQtyChange = (e) => {
    const value = e.target.value;
    if (Number(value) >= 0 && +value <= 9999) {
      handleAdjustmentInputChange(e);
      // setAdjustmentDisabledFields({
      //   quantity: value === "" ? false : true,
      //   noOfQty: false,
      //   unit: value === "" ? false : true
      // });
    }
  };

  useEffect(() => {
    dispatch(
      getAllMealOrRecipeData(type === "custom food" ? "customFood" : type)
    );
  }, [type]);

  const [openServicingRecipeModal, setOpenServicingRecipeModal] =
    useState(false);

  const updatedMealAccordingQuantity = useMemo(() => {
    if (type === "custom food") return allMealOrRecipeData;
    return allMealOrRecipeData?.map((singleItem) => {
      const arr = singleItem?.items?.map((item) => {
        const details = item.details;
        const metricServingAmount = parseFloat(
          details.serving.metric_serving_amount || item?.noOfQty
        );
        const isCustom = details?.isCustomFood || item?.type === "customFood";
        let customScalingFactor;
        if (isCustom) {
          customScalingFactor = item?.noOfQty
            ? item?.noOfQty
            : +convertItemQuantityToGrams(item).quantity /
            (details
              ? +convertItemQuantityToGrams(details)?.quantity || 1
              : 1);
        }
        const scalingFactor = isCustom
          ? customScalingFactor
          : convertItemQuantityToGrams(item, metricServingAmount).quantity /
          metricServingAmount;
        const updatedDetails = Object.entries(details.serving).reduce(
          (acc, [key, value]) => {
            if (!excludedFields.includes(key) && !isNaN(Number(value))) {
              acc[key] = (value * scalingFactor).toFixed(2);
            }
            return acc;
          },
          {}
        );
        return {
          ...item,
          details: {
            ...item.details,
            serving: { ...item.details.serving, ...updatedDetails }
          }
        };
      });
      return {
        ...singleItem,
        items: arr
      };
    });
  }, [allMealOrRecipeData]);

  const dispatch = useDispatch();
  const currentTime = dayjs().format("hh:mm A");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const adjustedNutritionData = useMemo(() => {
    if (!selectedCustomFoodItem || !showCustomFoodAdjustment) return null;

    const originalItem = selectedCustomFoodItem.items?.[0];
    if (!originalItem) return null;

    // Calculate scaling factor
    let scalingFactor = 1;
    if (adjustmentFormData.quantity && originalItem.quantity) {
      const adjustedWeight =
        convertItemQuantityToGrams(adjustmentFormData).quantity;
      const originalWeight = convertItemQuantityToGrams(originalItem).quantity;
      scalingFactor = adjustedWeight / originalWeight;
    } else if (adjustmentFormData.noOfQty && originalItem.noOfQty) {
      scalingFactor = adjustmentFormData.noOfQty / originalItem.noOfQty;
    } else if (adjustmentFormData.noOfQty && !originalItem.noOfQty) {
      scalingFactor = adjustmentFormData.noOfQty;
    } else if (adjustmentFormData.quantity && !originalItem.quantity) {
      const adjustedWeight =
        convertItemQuantityToGrams(adjustmentFormData).quantity;
      const originalWeight =
        convertItemQuantityToGrams(originalItem.details).quantity || 1;
      scalingFactor = adjustedWeight / originalWeight;
    }

    const serving = originalItem.details.serving;
    const adjustedServing = {};

    // Calculate adjusted values for all nutrients
    Object.entries(serving).forEach(([key, value]) => {
      if (!excludedFields.includes(key) && !isNaN(Number(value))) {
        adjustedServing[key] = (parseFloat(value) * scalingFactor).toFixed(2);
      } else {
        adjustedServing[key] = value;
      }
    });

    return {
      ...originalItem,
      details: {
        ...originalItem.details,
        serving: adjustedServing
      }
    };
  }, [selectedCustomFoodItem, adjustmentFormData, showCustomFoodAdjustment]);

  const handleAddToDiary = async (item) => {
    if (type === "custom food") {
      setSelectedCustomFoodItem(item);
      setShowCustomFoodAdjustment(true);

      // Set initial form data based on custom food's original data
      const originalItem = item.items?.[0];
      setAdjustmentFormData({
        quantity: originalItem?.quantity || "",
        noOfQty: originalItem?.noOfQty || "",
        unit: originalItem?.unit || "grams"
      });
      const scrollableElement = document.getElementById("dashboard-main"); // or document.body
      scrollableElement.scrollTo(0, 0);
      setAdjustmentDisabledFields({
        quantity: originalItem?.noOfQty ? true : false,
        noOfQty: originalItem?.quantity ? true : false,
        unit: true
      });
      return;
    }
    if (type === "recipe") {
      // Handle recipe inline instead of opening modal
      handleResetAmount?.();

      const total = item.items?.reduce(
        (acc, data) => {
          if (
            data.type === "800g" ||
            data.details?.isCustomFood ||
            type === "custom food"
          )
            acc.metric_serving_amount += parseFloat(
              data.details.serving?.metric_serving_amount
            );
          acc.calories += parseFloat(data.details?.serving?.calories) || 0;
          acc.protein += parseFloat(data.details?.serving?.protein) || 0;
          acc.carbohydrate +=
            parseFloat(data.details?.serving?.carbohydrate) || 0;
          acc.fat += parseFloat(data.details?.serving?.fat) || 0;
          acc.fiber += parseFloat(data.details?.serving?.fiber) || 0;
          acc.saturated_fat +=
            parseFloat(data.details?.serving?.saturated_fat) || 0;
          acc.sodium += parseFloat(data.details?.serving?.sodium) || 0;
          acc.potassium += parseFloat(data.details?.serving?.potassium) || 0;
          acc.vitamin_a += parseFloat(data.details?.serving?.vitamin_a) || 0;
          acc.vitamin_c += parseFloat(data.details?.serving?.vitamin_c) || 0;
          acc.vitamin_d += parseFloat(data.details?.serving?.vitamin_d) || 0;
          acc.calcium += parseFloat(data.details?.serving?.calcium) || 0;
          acc.iron += parseFloat(data.details?.serving?.iron) || 0;
          return acc;
        },
        {
          metric_serving_amount: 0,
          calories: 0,
          protein: 0,
          carbohydrate: 0,
          fat: 0,
          fiber: 0,
          saturated_fat: 0,
          sodium: 0,
          potassium: 0,
          vitamin_a: 0,
          vitamin_c: 0,
          vitamin_d: 0,
          calcium: 0,
          iron: 0
        }
      );

      const recipeData = {
        _id: item._id,
        name: item.name,
        recipeName: item.name,
        servicing_per_recipe: item.servicing_per_recipe,
        recipeType: item.recipeType,
        unit: item.unit,
        isRecipe: true,
        serving: {
          serving_id: item._id,
          metric_serving_amount: total.metric_serving_amount.toFixed(2),
          metric_serving_unit: "g",
          calories: total.calories.toFixed(2),
          protein: total.protein.toFixed(2),
          carbohydrate: (total.carbohydrate || 0).toFixed(2),
          fat: (total.fat || 0).toFixed(2),
          fiber: (total.fiber || 0).toFixed(2),
          saturated_fat: (total.saturated_fat || 0).toFixed(2),
          sodium: (total.sodium || 0).toFixed(2),
          potassium: (total.potassium || 0).toFixed(2),
          vitamin_a: (total.vitamin_a || 0).toFixed(2),
          vitamin_c: (total.vitamin_c || 0).toFixed(2),
          vitamin_d: (total.vitamin_d || 0).toFixed(2),
          calcium: (total.calcium || 0).toFixed(2),
          iron: (total.iron || 0).toFixed(2)
        },
        items: [
          {
            type: "800g",
            quantity:
              item.recipeType === "weight" ? item.servicing_per_recipe : "",
            noOfQty:
              item.recipeType === "service" ? item.servicing_per_recipe : "",
            unit:
              item.recipeType === "weight"
                ? item.unit === "grams"
                  ? "grams"
                  : item.unit === "pounds"
                    ? "pounds"
                    : "ounces"
                : "grams",
            details: {
              serving: {
                serving_id: item._id,
                metric_serving_amount: total.metric_serving_amount.toFixed(2),
                metric_serving_unit: "g",
                calories: total.calories.toFixed(2),
                protein: total.protein.toFixed(2),
                carbohydrate: (total.carbohydrate || 0).toFixed(2),
                fat: (total.fat || 0).toFixed(2),
                fiber: (total.fiber || 0).toFixed(2),
                saturated_fat: (total.saturated_fat || 0).toFixed(2),
                sodium: (total.sodium || 0).toFixed(2),
                potassium: (total.potassium || 0).toFixed(2),
                vitamin_a: (total.vitamin_a || 0).toFixed(2),
                vitamin_c: (total.vitamin_c || 0).toFixed(2),
                vitamin_d: (total.vitamin_d || 0).toFixed(2),
                calcium: (total.calcium || 0).toFixed(2),
                iron: (total.iron || 0).toFixed(2)
              }
            }
          }
        ]
      };

      setSelectedCustomFoodItem(recipeData);
      setSelectedCustomFood?.(recipeData);
      setSelectedOptionData?.(recipeData);
      setShowCustomFoodAdjustment(true);

      // Set initial form values based on recipe type
      setAdjustmentFormData({
        quantity: item.recipeType === "weight" ? item.servicing_per_recipe : "",
        noOfQty: item.recipeType === "service" ? item.servicing_per_recipe : "",
        unit:
          item.recipeType === "weight"
            ? item.unit === "grams"
              ? "grams"
              : item.unit === "pounds"
                ? "pounds"
                : "ounces"
            : "grams"
      });

      const scrollableElement = document.getElementById("dashboard-main");
      scrollableElement?.scrollTo(0, 0);

      setAdjustmentDisabledFields({
        quantity: item.recipeType === "service",
        noOfQty: item.recipeType === "weight",
        unit: true
      });

      return;
    } else {
      try {
        if (isSubmitting) return;

        // Set submission flag
        setIsSubmitting(true);
        const tomorrow = dayjs().add(1, "day").startOf("day");

        // Check if the input date is tomorrow
        const isTomorrow = dayjs(activeDay).isSame(tomorrow, "day");

        await addMealOrRecipeToDiary({
          levelId,
          type: type === "custom food" ? "customFood" : type,
          mealOrRecipeId: item._id,
          currentDayDate: activeDay,
          completionNotNeeded: isTomorrow,
          time: currentTime
        });
        type === "custom food"
          ? setEditCustomFoodToDiary?.(false)
          : setEditMealToDiary(false);
        setIsRefreshNeeded(true);
        setTimeout(() => {
          dispatch(
            getCurrentUserDiaries({
              levelId,
              activeDay
            })
          );
        }, 2000);
        localStorage.setItem(
          "lastDiaryVisitedLevel",
          JSON.stringify({
            level: currentLevel?.levelName,
            activeDay,
            lastUpdated: dayjs().format("YYYY-MM-DD")
          })
        );
      } catch (err) {
        setSnackbarMessage(err?.error || "Error while adding to diary");
        setOpenSnackbar(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  if (showCustomFoodAdjustment && selectedCustomFoodItem) {
    const adjustedData = adjustedNutritionData;

    // Format number function
    const formatAdjustedNumber = (value) => {
      if (!value || isNaN(value) || !+value) return "0";
      return value >= 1
        ? Math.round(value)?.toLocaleString()
        : parseFloat(value)?.toFixed(2);
    };

    return (
      <Box
        color={styles.color}
        backgroundColor={styles.background}
        display="flex"
        width="100%"
        sx={{ maxWidth: { xs: "100%", md: "60%" } }}
        overflow="auto"
        flexDirection="column"
      >
        <Box
          flexGrow={1}
          paddingY={3}
          sx={{
            paddingRight: { xs: 2, md: 13, lg: 20 },
            paddingBottom: { xs: 8, md: 2 },
            paddingLeft: { xs: 2, md: 7 },
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            borderRadius="8px"
            padding="14px"
            sx={{ background: styles.lightGreyColor }}
            mb={3}
          >
            <Typography>
              {selectedCustomFoodItem?.name}{" "}
              {type === "recipe"
                ? `(${selectedCustomFoodItem?.servicing_per_recipe}${selectedCustomFoodItem?.recipeType === "weight"
                  ? selectedCustomFoodItem?.unit === "grams"
                    ? "g"
                    : selectedCustomFoodItem?.unit === "pounds"
                      ? "lb"
                      : selectedCustomFoodItem?.unit === "ounces"
                        ? "oz"
                        : ""
                  : ""
                } ${selectedCustomFoodItem?.recipeType === "weight"
                  ? "Total"
                  : "Serv"
                })`
                : type === "custom food"
                  ? selectedCustomFoodItem?.items?.[0]?.quantity
                    ? `(${selectedCustomFoodItem?.items[0]?.quantity} ${selectedCustomFoodItem?.items[0]?.unit === "grams"
                      ? "g"
                      : selectedCustomFoodItem?.items[0]?.unit === "pounds"
                        ? "lb"
                        : selectedCustomFoodItem?.items[0]?.unit === "ounces"
                          ? "oz"
                          : selectedCustomFoodItem?.items[0]?.unit
                    })`
                    : "(1 Unit)"
                  : ""}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: "20px" },
              alignItems: { xs: "stretch", md: "center" },
              mb: 3,
              width: "100%"
            }}
          >
            {/* First Row (Quantity + Unit) */}
            {!adjustmentDisabledFields.quantity && (
              <Box sx={{ width: "100%" }}>
                {adjustmentDisabledFields.noOfQty && (
                  <Typography
                    sx={{
                      color: styles.inputLabelColor,
                      fontWeight: "bold",
                      fontSize: "14px",
                      mb: 1
                    }}
                  >
                    Enter {adjustmentFormData.unit}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 2, md: "20px" },
                    width: "100%",
                    maxWidth: adjustmentDisabledFields.unit ? "100%" : { xs: "100%", md: "60%" }
                  }}
                >
                  <TextField
                    type="number"
                    name="quantity"
                    placeholder="Amount"
                    value={adjustmentFormData.quantity}
                    onChange={handleAdjustmentQuantityChange}
                    disabled={adjustmentDisabledFields.quantity}
                    onWheel={(e) => e.target.blur()}
                    sx={{
                      flex: 1, // 60%
                      input: { color: styles.color },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: styles.inputLabelColor },
                        "&:hover fieldset": { borderColor: styles.inputLabelColor },
                        "&.Mui-focused fieldset": {
                          borderColor: styles.inputLabelColor
                        },
                        "&.Mui-disabled": {
                          background: styles.lightGreyColor,
                          color: styles.disabledColor,
                          "& fieldset": { borderColor: styles.inputLabelColor }
                        }
                      }
                    }}
                  />

                  {!adjustmentDisabledFields.unit && (
                    <Select
                      name="unit"
                      value={adjustmentFormData.unit}
                      onChange={handleAdjustmentInputChange}
                      disabled={adjustmentDisabledFields.unit}
                      displayEmpty
                      sx={{
                        flex: 2, // 40%
                        color: styles.color,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: styles.inputLabelColor
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: styles.inputLabelColor
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: styles.inputLabelColor
                        },
                        "&.Mui-disabled": {
                          background: styles.lightGreyColor,
                          color: styles.disabledColor,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: styles.inputLabelColor
                          }
                        }
                      }}
                    >
                      <MenuItem value="grams">Grams</MenuItem>
                      <MenuItem value="ounces">Ounces</MenuItem>
                      <MenuItem value="cups">Cups / Fists</MenuItem>
                      <MenuItem value="palms">Palms</MenuItem>
                      <MenuItem value="pounds">Pounds</MenuItem>
                    </Select>
                  )}
                </Box>
              </Box>
            )}

            {/* OR text */}
            {!adjustmentDisabledFields.quantity && !adjustmentDisabledFields.noOfQty && (
              <Typography
                display="flex"
                fontWeight="bold"
                justifyContent="center"
                mt={{ xs: 1, md: 0 }}
              >
                OR
              </Typography>
            )}

            {/* Second Row (No of Qty) */}
            {!adjustmentDisabledFields.noOfQty && (
              <Box sx={{ flex: 3, width: "100%" }}>
                {adjustmentDisabledFields.quantity && (
                  <Typography
                    sx={{
                      color: styles.inputLabelColor,
                      fontWeight: "bold",
                      fontSize: "14px",
                      mb: 1
                    }}
                  >
                    Enter servings
                  </Typography>
                )}
                <TextField
                  type="number"
                  name="noOfQty"
                  placeholder="Quantity"
                  value={adjustmentFormData.noOfQty}
                  onChange={handleAdjustmentNoOfQtyChange}
                  disabled={adjustmentDisabledFields.noOfQty}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    flex: 3,
                    width: "100%",
                    input: { color: styles.color },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: styles.inputLabelColor },
                      "&:hover fieldset": { borderColor: styles.inputLabelColor },
                      "&.Mui-focused fieldset": {
                        borderColor: styles.inputLabelColor
                      },
                      "&.Mui-disabled": {
                        background: styles.lightGreyColor,
                        color: styles.disabledColor,
                        "& fieldset": { borderColor: styles.inputLabelColor }
                      }
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Pillar Label (disabled display) */}
          {selectedCustomFoodItem.items?.[0]?.type && !addingRecipeToDiary && (
            <Box sx={{ mt: 0, mb: 1 }}>
              <Typography
                sx={{
                  color: styles.inputLabelColor,
                  fontWeight: "bold",
                  fontSize: "14px",
                  mb: 1
                }}
              >
                Pillar Label
              </Typography>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                mt={1}
                width="100%"
                borderRadius="8px"
                padding="12px"
                sx={{ background: styles.lightGreyColor }}
              >
                <Typography textTransform="capitalize">
                  {selectedCustomFoodItem.items?.[0]?.type}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Three Pillars Method */}
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography
              sx={{
                color: styles.inputLabelColor,
                fontWeight: "bold",
                fontSize: "14px",
                mb: 2
              }}
            >
              Nutrition Data
            </Typography>
            <Box mt={2} width="100%" borderRadius={2} display="flex" gap="28px">
              <Box
                width="33%"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                display="flex"
                gap={1}
                sx={{
                  background: styles.modBackgroundBlocks,
                  padding: isMobile ? "14px 28px" : "14px 50px"
                }}
                borderRadius="8px"
              >
                <Typography
                  fontSize="12px"
                  display="flex"
                  justifyContent="center"
                  fontWeight={400}
                  color={styles.inputLabelColor}
                >
                  800g
                </Typography>
                <Typography fontSize="24px" fontWeight={600} color="#F6541C">
                  {selectedCustomFoodItem.items?.[0]?.type === "800g"
                    ? formatAdjustedNumber(
                      adjustedData?.details?.serving?.metric_serving_amount ||
                      0
                    )
                    : "0"}
                </Typography>
              </Box>

              <Box
                width="33%"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                display="flex"
                gap={1}
                sx={{
                  background: styles.modBackgroundBlocks,
                  padding: isMobile ? "14px 28px" : "14px 50px"
                }}
                borderRadius="8px"
              >
                <Typography
                  fontSize="12px"
                  fontWeight={400}
                  color={styles.inputLabelColor}
                >
                  Protein
                </Typography>
                <Typography fontSize="24px" fontWeight={600} color="#2C75C5">
                  {formatAdjustedNumber(
                    adjustedData?.details?.serving?.protein || 0
                  )}
                </Typography>
              </Box>

              <Box
                width="33%"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                display="flex"
                gap={1}
                sx={{
                  background: styles.modBackgroundBlocks,
                  padding: isMobile ? "14px 28px" : "14px 50px"
                }}
                borderRadius="8px"
              >
                <Typography
                  fontSize="12px"
                  fontWeight={400}
                  color={styles.inputLabelColor}
                >
                  Calories
                </Typography>
                <Typography fontSize="24px" fontWeight={600} color="#FF9431">
                  {formatAdjustedNumber(
                    adjustedData?.details?.serving?.calories || 0
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Add to Diary Button */}
          <Button
            onClick={async () => {
              try {
                if (isSubmitting) return;
                setIsSubmitting(true);

                const tomorrow = dayjs().add(1, "day").startOf("day");
                const isTomorrow = dayjs(activeDay).isSame(tomorrow, "day");

                await addMealOrRecipeToDiary({
                  levelId,
                  type: addingRecipeToDiary ? "recipe" : "customFood",
                  mealOrRecipeId: selectedCustomFoodItem._id,
                  currentDayDate: activeDay,
                  completionNotNeeded: isTomorrow,
                  time: currentTime,
                  ...(addingRecipeToDiary && {
                    totalConsumed:
                      adjustmentFormData?.noOfQty ||
                      adjustmentFormData?.quantity
                  }),
                  customQuantity: adjustmentFormData.quantity,
                  customNoOfQty: adjustmentFormData.noOfQty,
                  customUnit: adjustmentFormData.unit
                });

                // dispatch(addNotificationUser(dayjs().format("YYYY-MM-DD")));
                setEditCustomFoodToDiary?.(false);
                if (addingRecipeToDiary) {
                  setAddingRecipeToDiary(false);
                  setEditRecipeToDiary?.(false);
                }
                setIsRefreshNeeded(true);
                setShowCustomFoodAdjustment(false);
                setSelectedCustomFoodItem(null);

                setTimeout(() => {
                  dispatch(getCurrentUserDiaries({ levelId, activeDay }));
                }, 2000);

                localStorage.setItem(
                  "lastDiaryVisitedLevel",
                  JSON.stringify({
                    level: currentLevel?.levelName,
                    activeDay,
                    lastUpdated: dayjs().format("YYYY-MM-DD")
                  })
                );
              } catch (err) {
                setSnackbarMessage(err?.error || "Error while adding to diary");
                setOpenSnackbar(true);
              } finally {
                setIsSubmitting(false);
              }
            }}
            fullWidth
            disabled={
              (!adjustmentFormData.quantity && !adjustmentFormData.noOfQty) ||
              isSubmitting
            }
            variant="contained"
            sx={{
              mb: 3,
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#368AD0",
              ":disabled": { backgroundColor: "#A7C8E9", color: styles.color }
            }}
          >
            {isSubmitting ? "Adding..." : "Add to Diary"}
          </Button>
          {/* Additional Stats */}
          <Box>
            <Typography
              sx={{
                color: styles.inputLabelColor,
                fontWeight: "bold",
                fontSize: "14px"
              }}
            >
              Additional Stats
            </Typography>
            <Box width="100%" pt={1} borderRadius={1}>
              {/* Carbs Group */}
              <Box mb={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Carbohydrate</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.carbohydrate || 0
                    )}
                    g
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Fiber</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.fiber || 0
                    )}
                    g
                  </Typography>
                </Box>
              </Box>

              {/* Fats Group */}
              <Box mb={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Fat</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.fat || 0
                    )}
                    g
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Saturated Fat</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.saturated_fat || 0
                    )}
                    g
                  </Typography>
                </Box>
              </Box>

              {/* Vitamins and Minerals Group */}
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Calcium</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.calcium || 0
                    )}
                    mg
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Iron</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.iron || 0
                    )}
                    mg
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Potassium</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.potassium || 0
                    )}
                    mg
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Sodium</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.sodium || 0
                    )}
                    mg
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Vitamin A</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.vitamin_a || 0
                    )}
                    mcg
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Vitamin C</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.vitamin_c || 0
                    )}
                    mg
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.5}
                >
                  <Typography fontSize="13px">Vitamin D</Typography>
                  <Typography fontSize="13px">
                    {formatAdjustedNumber(
                      adjustedData?.details?.serving?.vitamin_d || 0
                    )}
                    mcg
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box
      color={styles.color}
      backgroundColor={styles.background}
      display={"flex"}
      width="100%"
      overflow="auto"
      flexDirection="column"
    >
      <Box
        flexGrow={1}
        paddingY={3}
        paddingLeft={10}
        sx={{
          paddingTop: { xs: 2, md: 2 },
          paddingRight: { xs: 2 },
          paddingBottom: { xs: 8, md: 2 },
          paddingLeft: { xs: 2, md: 7 },
          overflowY: { md: "auto" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start"
        }}
      >
        <Box
          gap="20px"
          justifyContent="space-between"
          sx={{
            // width: isMobile ? "100%" : "70%",
            alignItems: isMobile ? "start" : "center",
            display: isMobile ? "block" : "flex",
            flexDirection: isMobile ? "column" : "row"
          }}
        >
          <TextField
            variant="outlined" // You can change this to "filled" or "standard" as needed
            placeholder={`Search ${type}...`}
            size="small" // Adjust size if needed
            fullWidth // Makes the search bar take full width
            onChange={(e) => setSearchValue(e.target.value)}
            value={searchValue}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ height: "17px", color: "#4B5563" }} />
                  </InputAdornment>
                )
              }
            }}
            sx={{
              width: { xs: "100%", md: "70%" },
              "& .MuiInputBase-root": {
                padding: "14px 11px",
                height: "100%",
                //   width: "387px",
                borderRadius: 2,
                background: styles.trackerBackground,
                color: "#6B7280",
                "& fieldset": {
                  borderColor: "#358ad0 !important"
                }
              },
              "& .Mui-focused": {
                "& fieldset": {
                  borderColor: "#358ad0 !important"
                }
              },
              height: "48px",
              // width: !isMobile ? "387px" : "unset",
              fontSize: "14px",
              background: styles.background, // Optional: set background color
              borderRadius: 2 // Optional: round corners
            }}
          />
          <Button
            onClick={() => {
              setEditing(false);
              if (type === "recipe") {
                setIsShowingCreateRecipe(true);
                setEditRecipeToDiary(false);
                dispatch(updateAddingRecipe({ recipe: {} }));
              } else if (type === "meal") {
                setIsShowingCreateMeal(true);
                setEditMealToDiary(false);
                dispatch(updateAddingMeal({ meal: {} }));
              } else {
                setIsShowingCreateCustomFood(true);
                setEditCustomFoodToDiary?.(false);
                setCustomFoodFormData({
                  name: "",
                  quantity: "",
                  noOfQty: "",
                  unit: "grams",
                  type: "800g",
                  details: {
                    serving: {
                      serving_id: String(
                        Math.floor(Math.random() * 90000) + 10000
                      ),
                      metric_serving_amount: "",
                      metric_serving_unit: "g",
                      calories: "",
                      carbohydrate: "",
                      protein: "",
                      fat: "",
                      saturated_fat: "",
                      sodium: "",
                      potassium: "",
                      fiber: "",
                      vitamin_a: "",
                      vitamin_c: "",
                      vitamin_d: "",
                      calcium: "",
                      iron: ""
                    }
                  }
                });
              }
            }}
            sx={{
              mt: isMobile ? 2 : 0,
              textTransform: "none",
              background: "#368AD0",
              color: "#fcfcfc",
              height: "40px",
              fontWeight: 600,
              width: { md: "190px", xs: "100%" },
              padding: "12px 0",
              gap: "8px",
              borderRadius: "4px"
            }}
          >
            Create new {type}
          </Button>
        </Box>
        <Box mt={3}>
          {processMeals(updatedMealAccordingQuantity, searchValue, type)
            ?.length ? (
            processMeals(updatedMealAccordingQuantity, searchValue, type)?.map(
              (item) => (
                <>
                  <Paper
                    // onClick={async () => await handleAddToDiary(item)}
                    key={item.id}
                    sx={{
                      boxShadow: "none",
                      background: "transparent",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: 2,
                      color: styles.color,
                      cursor: "pointer"
                    }}
                  >
                    <Box>
                      <Typography fontSize={"15px"}>
                        {item.name}{" "}
                        {type === "recipe"
                          ? `(${item?.servicing_per_recipe}${item?.recipeType === "weight"
                            ? item?.unit === "grams"
                              ? "g"
                              : item?.unit === "pounds"
                                ? "lb"
                                : item?.unit === "ounces"
                                  ? "oz"
                                  : ""
                            : ""
                          } ${item?.recipeType === "weight" ? "Total" : "Serv"
                          })`
                          : type === "custom food"
                            ? item?.items[0]?.quantity
                              ? `(${item?.items[0]?.quantity} ${item?.items[0]?.unit === "grams"
                                ? "g"
                                : item?.items[0]?.unit === "pounds"
                                  ? "lb"
                                  : item?.items[0]?.unit === "ounces"
                                    ? "oz"
                                    : item?.items[0]?.unit
                              })`
                              : "(1 Unit)"
                            : ""}
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1.1}
                        width="140px"
                      >
                        <Typography
                          justifyContent="center"
                          alignItems="center"
                          gap="4px"
                          display="flex"
                          fontSize="13px"
                          fontWeight="bold"
                          color={styles.color}
                        >
                          <Box
                            height="8px"
                            width="8px"
                            sx={{ background: "#F6541C" }}
                            borderRadius="50%"
                          />
                          {formatNumber(item.metric_serving_amount || 0)}g
                        </Typography>
                        <Typography
                          justifyContent="center"
                          alignItems="center"
                          gap="4px"
                          display="flex"
                          fontSize="13px"
                          fontWeight="bold"
                          color={styles.color}
                        >
                          <Box
                            height="8px"
                            width="8px"
                            sx={{ background: "#368AD0" }}
                            borderRadius="50%"
                          />
                          {formatNumber(item.protein || 0)}g
                        </Typography>
                        <Typography
                          justifyContent="center"
                          alignItems="center"
                          gap="4px"
                          display="flex"
                          fontSize="13px"
                          fontWeight="bold"
                          color={styles.color}
                        >
                          <Box
                            height="8px"
                            width="8px"
                            sx={{ background: "#FF9431" }}
                            borderRadius="50%"
                          />
                          {Math.round(formatNumber(item.calories || 0))}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      display={"flex"}
                      gap="10px"
                      alignItems="center"
                      justifyContent={"center"}
                    >
                      <IconButton
                        onClick={async () => {
                          await handleAddToDiary(item);
                          if (type === "recipe") setAddingRecipeToDiary(true);
                        }}
                        sx={{
                          background: styles.primaryColor,
                          color: "#FFF",
                          height: "17px",
                          width: "17px",
                          borderRadius: 1,
                          padding: 2,
                          ":hover": {
                            background: "#64AEDD",
                            color: "#FFF"
                          }
                        }}
                      // disabled
                      >
                        <Add color={styles.textColorDiary} fontSize="10px" />
                      </IconButton>
                      <LongMenu
                        onClickHandler={async (actionType) => {
                          // setEditIngredientData(item);
                          if (actionType === "edit") {
                            setEditing(true);
                            // setOpenEditMealItemModal(item);
                            if (type === "meal") {
                              setEditMealToDiary(false);
                              await dispatch(gettingMealData(item._id));
                              setIsShowingCreateMeal(true);
                            } else if (type === "recipe") {
                              setEditRecipeToDiary(false);
                              await dispatch(gettingRecipeData(item._id));
                              setIsShowingCreateRecipe(true);
                            } else {
                              setEditCustomFoodToDiary?.(false);
                              setCustomFoodFormData({
                                id: item?._id,
                                ...item?.items[0]
                              });
                              setIsShowingCreateCustomFood(true);
                            }
                          } else {
                            setDeleteCustomFoodItemId(item?._id);
                            setOpenDeleteModal(true);
                          }
                        }}
                      />
                    </Box>
                  </Paper>
                  <Divider sx={{ margin: "0 16px", background: "#333333" }} />
                  <DeleteUserModal
                    open={openDeleteModal}
                    handleClose={() => setOpenDeleteModal(false)}
                    handleDelete={async () => {
                      if (type !== "custom food") {
                        await deleteMealRecipe(deleteCustomFoodItemId, type);
                      } else {
                        await deleteCustomFoodItem(deleteCustomFoodItemId);
                      }
                      await dispatch(
                        getAllMealOrRecipeData(
                          type === "custom food" ? "customFood" : type
                        )
                      );
                      setOpenDeleteModal(false);
                    }}
                  />
                </>
              )
            )
          ) : (
            <></>
          )}
        </Box>
      </Box>
      <ServicingRecipeModal
        open={openServicingRecipeModal}
        setOpen={setOpenServicingRecipeModal}
        handleSubmit={async (e, amount) => {
          try {
            if (isSubmitting) return;
            setIsSubmitting(true);
            e.preventDefault();
            e.stopPropagation();
            const tomorrow = dayjs().add(1, "day").startOf("day");

            // Check if the input date is tomorrow
            const isTomorrow = dayjs(activeDay).isSame(tomorrow, "day");
            await addMealOrRecipeToDiary({
              levelId,
              type,
              mealOrRecipeId: openServicingRecipeModal._id,
              currentDayDate: activeDay,
              time: currentTime,
              totalConsumed: amount,
              completionNotNeeded: isTomorrow
            });
            setEditRecipeToDiary(false);
            setIsRefreshNeeded(true);
            setTimeout(() => {
              dispatch(
                getCurrentUserDiaries({
                  levelId,
                  activeDay
                })
              );
            }, 2000);
            localStorage.setItem(
              "lastDiaryVisitedLevel",
              JSON.stringify({
                level: currentLevel?.levelName,
                activeDay,
                lastUpdated: dayjs().format("YYYY-MM-DD")
              })
            );
          } catch (err) {
            setSnackbarMessage(err?.error || "Error while adding to diary");
            setOpenSnackbar(true);
          } finally {
            setIsSubmitting(false);
          }
        }}
      />
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarMessage?.includes("Success") ? "success" : "error"}
          variant="filled"
          sx={{
            width: "100%",
            alignItems: "center",
            padding: "0 16px",
            ".MuiAlert-action": {
              padding: "8px"
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddMealOrRecipeToDiary;
